import { OnboardingPreferences } from '../types/onboarding';

const ONBOARDING_STORAGE_KEY = 'onboarding_preferences';
const SESSION_ID_KEY = 'onboarding_session_id';

/**
 * Session storage utilities for onboarding flow
 * Manages "remind me later" preferences within browser session
 */
export class OnboardingSessionStorage {
  /**
   * Generate or retrieve current session ID
   */
  private static getSessionId(): string {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);

    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      try {
        sessionStorage.setItem(SESSION_ID_KEY, sessionId);
      } catch (error) {
        console.error('[OnboardingSessionStorage] Failed to save session ID:', error);
      }
    }

    return sessionId;
  }

  /**
   * Save "remind me later" preference to session storage
   * @param userId - User's UUID
   * @returns boolean indicating success
   */
  static saveRemindLaterPreference(userId: string): boolean {
    try {
      const preferences: OnboardingPreferences = {
        remindLaterTimestamp: Date.now(),
        sessionId: this.getSessionId(),
        userId,
      };

      sessionStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(preferences));
      console.log('[OnboardingSessionStorage] Saved remind later preference:', preferences);
      return true;
    } catch (error) {
      console.error('[OnboardingSessionStorage] Failed to save preference:', error);
      return false;
    }
  }

  /**
   * Check if user has previously selected "remind me later" in current session
   * @param userId - User's UUID
   * @returns boolean indicating if user dismissed the modal
   */
  static hasRemindLaterPreference(userId: string): boolean {
    try {
      const stored = sessionStorage.getItem(ONBOARDING_STORAGE_KEY);

      if (!stored) {
        return false;
      }

      const preferences: OnboardingPreferences = JSON.parse(stored);

      // Check if preference matches current user (ignore event)
      const matches = preferences.userId === userId;

      if (matches) {
        console.log('[OnboardingSessionStorage] Found existing remind later preference');
      }

      return matches;
    } catch (error) {
      console.error('[OnboardingSessionStorage] Failed to read preference:', error);
      return false;
    }
  }

  /**
   * Clear onboarding preferences (useful for testing or manual reset)
   */
  static clearPreferences(): void {
    try {
      sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
      console.log('[OnboardingSessionStorage] Cleared preferences');
    } catch (error) {
      console.error('[OnboardingSessionStorage] Failed to clear preferences:', error);
    }
  }

  /**
   * Get current session ID
   */
  static getCurrentSessionId(): string {
    return this.getSessionId();
  }

  /**
   * Check if session storage is available
   */
  static isAvailable(): boolean {
    try {
      const testKey = '__onboarding_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }
}
